import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { shopify } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { t } from "@/lib/i18n";

const CATEGORY_CHIPS = [
  { label: "Alle", to: "/shop", matchOn: "/shop" },
  { label: "Hoodies", to: "/collections/marcini-hoodies-unisex", matchOn: "marcini-hoodies-unisex" },
  { label: "T-skjorter", to: "/collections/marcini-t-shirts-unisex", matchOn: "marcini-t-shirts-unisex" },
  { label: "Caps", to: "/collections/marcini-capses-unisex", matchOn: "marcini-capses-unisex" },
  { label: "Nyheter", to: "/collections/nye-produkter", matchOn: "nye-produkter" },
];

export default function Shop() {
  const { handle } = useParams();
  const location = useLocation();
  const [sort, setSort] = useState("featured");
  const [availOnly, setAvailOnly] = useState(false);
  const [query, setQuery] = useState("");

  const { data: collection } = useQuery({
    queryKey: ["collection", handle],
    queryFn: () => shopify.collection(handle, "NO"),
    enabled: !!handle,
  });
  const { data: allProducts = [] } = useQuery({
    queryKey: ["shop-products", handle, query],
    queryFn: () => shopify.products("NO", handle, query || undefined),
  });

  const products = collection?.products || allProducts;

  const sorted = useMemo(() => {
    const arr = [...products];
    if (availOnly) return arr.filter(p => p.available).sort((a, b) => sortFn(sort, a, b));
    return arr.sort((a, b) => sortFn(sort, a, b));
  }, [products, sort, availOnly]);

  return (
    <div className="pt-24 md:pt-32 px-5 md:px-12">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-6 md:mb-8">
        <div>
          <p className="text-eyebrow mb-2">{collection ? "Kolleksjon" : t.shop.eyebrow}</p>
          <h1 className="font-serif-editorial text-5xl md:text-8xl leading-[0.9]">
            {collection?.title || t.shop.title}
          </h1>
          {collection?.description && (
            <p className="mt-4 text-[color:var(--marcini-fg-2)] max-w-lg">{collection.description}</p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-4 md:gap-6 text-eyebrow">
          <input
            data-testid="shop-search"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={t.shop.searchPlaceholder}
            className="bg-transparent border-b border-black py-1 px-1 outline-none text-eyebrow min-w-[100px]"
          />
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" data-testid="shop-avail-only" checked={availOnly} onChange={e => setAvailOnly(e.target.checked)} />
            <span>{t.shop.inStockOnly}</span>
          </label>
          <select data-testid="shop-sort" value={sort} onChange={e => setSort(e.target.value)} className="bg-transparent border-b border-black py-1 text-eyebrow" aria-label="Sorter">
            <option value="featured">{t.shop.sort.featured}</option>
            <option value="price-asc">{t.shop.sort.priceAsc}</option>
            <option value="price-desc">{t.shop.sort.priceDesc}</option>
            <option value="title">{t.shop.sort.title}</option>
          </select>
        </div>
      </div>

      {/* Category filter chips — quick navigation between hoodies / t-shirts / caps / all */}
      <div className="flex gap-2 md:gap-3 mb-10 md:mb-12 overflow-x-auto -mx-5 md:-mx-0 px-5 md:px-0 pb-2" data-testid="category-chips">
        {CATEGORY_CHIPS.map(chip => {
          const active = chip.matchOn === "/shop"
            ? location.pathname === "/shop"
            : location.pathname.includes(chip.matchOn);
          return (
            <Link
              key={chip.to}
              to={chip.to}
              data-testid={`chip-${chip.matchOn}`}
              className={`text-eyebrow px-4 py-2 border whitespace-nowrap transition-colors ${
                active
                  ? "bg-black text-white border-black"
                  : "bg-transparent text-black border-[color:var(--marcini-border)] hover:border-black"
              }`}
            >
              {chip.label}
            </Link>
          );
        })}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-10 pb-20 md:pb-24">
        {sorted.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        {sorted.length === 0 && (
          <p data-testid="shop-empty" className="col-span-full text-center py-24 text-[color:var(--marcini-fg-2)]">
            {t.shop.empty}
          </p>
        )}
      </div>
    </div>
  );
}

function sortFn(mode, a, b) {
  if (mode === "price-asc") return parseFloat(a.price_range.min.amount) - parseFloat(b.price_range.min.amount);
  if (mode === "price-desc") return parseFloat(b.price_range.min.amount) - parseFloat(a.price_range.min.amount);
  if (mode === "title") return a.title.localeCompare(b.title, "nb");
  return 0;
}
